import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { XMarkIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import AuthRequiredModal from './AuthRequiredModal';
import { useModalLayer } from '../hooks/useModalLayer';

const ShoppingCart: React.FC = () => {
  const navigate = useNavigate();
  const { items, total, isOpen, toggleCart, removeItem, clearCart } = useCart();
  const { isAuthenticated } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  /** Avoid double Escape / scroll-lock when auth modal stacks over the cart. */
  useModalLayer(isOpen && !showAuthModal, toggleCart);

  const handleCheckout = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    toggleCart();
    navigate('/checkout');
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black z-40"
              onClick={toggleCart}
              aria-hidden
            />

            {/* Cart Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
              className="fixed right-0 top-0 h-full w-full max-w-md bg-dark-lighter z-50 shadow-xl"
            >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h2 className="text-xl font-bold">Shopping Cart</h2>
              <button
                type="button"
                onClick={toggleCart}
                className="p-2 hover:bg-white/5 rounded-full transition-colors"
                aria-label="Close cart"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto p-4">
              {items.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-400 mb-4">Your cart is empty</p>
                  <Link
                    to="/samples"
                    onClick={toggleCart}
                    className="inline-block neon-button-blue px-6 py-2 rounded-full text-sm font-medium"
                  >
                    Browse sample packs
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-4 bg-dark rounded-lg"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="w-16 h-16 relative rounded-lg overflow-hidden flex-shrink-0">
                          <div className={`absolute inset-0 opacity-30 ${
                            item.type === 'preset' || item.type === 'bundle' ? 'bg-product-blue' : 'bg-product-red'
                          }`} />
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-full h-full object-cover"
                            onError={(e) => { (e.target as HTMLImageElement).src = '/sample-box.png'; }}
                          />
                        </div>
                        <div>
                          <h3 className="font-medium">{item.name}</h3>
                          <p className="text-sm text-gray-400">{item.type.replace('_', ' ')}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <p className="font-medium">${item.price.toFixed(2)}</p>
                        <button
                          type="button"
                          onClick={() => removeItem(item.id)}
                          className="p-2 text-red-500 hover:bg-red-500/10 rounded-full transition-colors"
                          aria-label={`Remove ${item.name} from cart`}
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-white/10 p-4">
              {items.length > 0 && (
                <div className="mb-4 flex items-center justify-between text-sm">
                  <button
                    onClick={clearCart}
                    className="text-red-500 hover:text-red-400 transition-colors"
                  >
                    Clear Cart
                  </button>
                  <div className="font-medium">
                    Total: <span className="text-lg">${total.toFixed(2)}</span>
                  </div>
                </div>
              )}
              <button
                type="button"
                disabled={items.length === 0}
                onClick={handleCheckout}
                className={`w-full neon-button-blue px-6 py-3 rounded-full font-medium
                  ${items.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}
                `}
              >
                Checkout
              </button>
            </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      <AuthRequiredModal
        open={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSignIn={() => {
          setShowAuthModal(false);
          toggleCart();
          navigate('/account');
        }}
      />
    </>
  );
};

export default ShoppingCart; 